/** Shared server-sort editor; feature code supplies the supported public field names. */
import { ArrowDown, ArrowDownUp, ArrowUp, Plus, RotateCcw, Trash2 } from "lucide-react";
import { type ReactElement } from "react";
import { useTranslation } from "react-i18next";

import { parseSorting, toOrdering } from "@/shared/components/data-table";
import { type TableFacetOption } from "@/shared/components/table-facet";
import { Button } from "@/shared/ui/button";
import { Select } from "@/shared/ui/form";
import { Popover, PopoverContent, PopoverTrigger } from "@/shared/ui/popover";

/** Edit multi-column ordering without sorting only the loaded server page. */
export function TableSortMenu({
  fields,
  value,
  onChange,
}: {
  fields: TableFacetOption[];
  value?: string;
  onChange: (value: string | undefined) => void;
}): ReactElement {
  const { t } = useTranslation();
  const sorts = parseSorting(value);
  const unused = fields.filter((field) => !sorts.some((sort) => sort.id === field.value));
  const move = (index: number, direction: number): void => {
    const next = [...sorts];
    const current = next[index];
    const neighbor = next[index + direction];
    if (current && neighbor) {
      next[index] = neighbor;
      next[index + direction] = current;
      onChange(toOrdering(next));
    }
  };
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className="gap-2 sm:ms-auto"
          aria-label={t("tablePilot.sort")}
        >
          <ArrowDownUp className="size-4" aria-hidden />
          {t("tablePilot.sort")}
          <span className="rounded bg-muted px-1.5 tabular-nums">{sorts.length}</span>
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="flex w-[30rem] flex-col overflow-hidden"
        aria-label={t("tablePilot.sort")}
      >
        <h2 className="shrink-0 border-b p-3 text-sm font-semibold">{t("tablePilot.sort")}</h2>
        <div className="min-h-0 space-y-3 overflow-y-auto p-3">
          {sorts.map((sort, index) => (
            <div key={sort.id} className="flex flex-wrap items-center gap-2">
              <span className="w-4 text-xs tabular-nums text-muted-foreground">{index + 1}</span>
              <Select
                aria-label={t("tablePilot.sortField", { index: index + 1 })}
                className="min-w-32 flex-1"
                value={sort.id}
                onChange={(event) =>
                  onChange(
                    toOrdering(
                      sorts.map((entry, position) =>
                        position === index ? { ...entry, id: event.target.value } : entry,
                      ),
                    ),
                  )
                }
              >
                {fields
                  .filter((field) => field.value === sort.id || unused.includes(field))
                  .map((field) => (
                    <option key={field.value} value={field.value}>
                      {field.label}
                    </option>
                  ))}
              </Select>
              <Select
                aria-label={t("tablePilot.sortDirection", { index: index + 1 })}
                className="w-32"
                value={sort.desc ? "desc" : "asc"}
                onChange={(event) =>
                  onChange(
                    toOrdering(
                      sorts.map((entry, position) =>
                        position === index
                          ? { ...entry, desc: event.target.value === "desc" }
                          : entry,
                      ),
                    ),
                  )
                }
              >
                <option value="asc">{t("tablePilot.ascending")}</option>
                <option value="desc">{t("tablePilot.descending")}</option>
              </Select>
              <div className="ms-auto flex gap-1">
                <Button
                  size="icon"
                  variant="ghost"
                  disabled={index === 0}
                  title={t("app.moveUp")}
                  aria-label={t("tablePilot.moveSort", { index: index + 1 })}
                  onClick={() => move(index, -1)}
                >
                  <ArrowUp className="size-3.5" aria-hidden />
                </Button>
                <Button
                  size="icon"
                  variant="ghost"
                  disabled={index === sorts.length - 1}
                  title={t("app.moveDown")}
                  aria-label={t("tablePilot.moveSortDown", { index: index + 1 })}
                  onClick={() => move(index, 1)}
                >
                  <ArrowDown className="size-3.5" aria-hidden />
                </Button>
                <Button
                  size="icon"
                  variant="ghost"
                  disabled={sorts.length === 1}
                  title={t("app.remove")}
                  aria-label={t("tablePilot.removeSort", { index: index + 1 })}
                  onClick={() =>
                    onChange(toOrdering(sorts.filter((_, position) => position !== index)))
                  }
                >
                  <Trash2 className="size-3.5" aria-hidden />
                </Button>
              </div>
            </div>
          ))}
        </div>
        <div className="flex shrink-0 flex-wrap gap-2 border-t p-3">
          <Button
            variant="outline"
            size="sm"
            disabled={!unused.length}
            onClick={() => {
              const field = unused[0];
              if (field) onChange(toOrdering([...sorts, { id: field.value, desc: false }]));
            }}
          >
            <Plus className="size-3.5" aria-hidden />
            {t("tablePilot.addSort")}
          </Button>
          <Button variant="ghost" size="sm" onClick={() => onChange(undefined)}>
            <RotateCcw className="size-3.5" aria-hidden />
            {t("tablePilot.resetSort")}
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
